// https://github.com/WICG/focus-visible/blob/v5.0.2/src/focus-visible.js
import * as React from 'react';
import * as ReactDOM from 'react-dom';
var inputTypesWhitelist = {
  text: true,
  search: true,
  url: true,
  tel: true,
  email: true,
  password: true,
  number: true,
  date: true,
  month: true,
  week: true,
  time: true,
  datetime: true,
  'datetime-local': true
};
function focusTriggersKeyboardModality(el) {
  var {
    tagName
  } = el;
  if (el instanceof HTMLInputElement) {
    var {
      type
    } = el;
    if (tagName === 'INPUT' && inputTypesWhitelist[type] && !el.readOnly) {
      return true;
    }
    if (tagName === 'TEXTAREA' && !el.readOnly) {
      return true;
    }
  }
  return el.isContentEditable;
}
export default function useFocusVisible() {
  var hadFocusVisibleRecently = React.useRef(false);
  var hadFocusVisibleRecentlyTimeout = React.useRef();
  var hadKeyboardEvent = React.useRef(true);
  var ownerDocumentRef = React.useRef();
  var ref = React.useCallback(instance => {
    var node = ReactDOM.findDOMNode(instance);
    if (node) {
      ownerDocumentRef.current = node.ownerDocument;
    }
  }, []);
  var handleKeyDown = React.useCallback(event => {
    if (event.metaKey || event.altKey || event.ctrlKey) {
      return;
    }
    hadKeyboardEvent.current = true;
  }, []);
  var handlePointerDown = React.useCallback(() => {
    hadKeyboardEvent.current = false;
  }, []);
  var handleVisibilityChange = React.useCallback(() => {
    var {
      current: ownerDocument
    } = ownerDocumentRef;
    if ((ownerDocument === null || ownerDocument === void 0 ? void 0 : ownerDocument.visibilityState) === 'hidden' && hadFocusVisibleRecently.current) {
      hadKeyboardEvent.current = true;
    }
  }, []);
  React.useEffect(() => {
    var {
      current: ownerDocument
    } = ownerDocumentRef;
    if (ownerDocument) {
      ownerDocument.addEventListener('keydown', handleKeyDown, true);
      ownerDocument.addEventListener('mousedown', handlePointerDown, true);
      ownerDocument.addEventListener('pointerdown', handlePointerDown, true);
      ownerDocument.addEventListener('touchstart', handlePointerDown, true);
      ownerDocument.addEventListener('visibilitychange', handleVisibilityChange, true);
    }
    return () => {
      if (ownerDocument) {
        ownerDocument.removeEventListener('keydown', handleKeyDown, true);
        ownerDocument.removeEventListener('mousedown', handlePointerDown, true);
        ownerDocument.removeEventListener('pointerdown', handlePointerDown, true);
        ownerDocument.removeEventListener('touchstart', handlePointerDown, true);
        ownerDocument.removeEventListener('visibilitychange', handleVisibilityChange, true);
        clearTimeout(hadFocusVisibleRecentlyTimeout.current);
      }
    };
  }, [handleKeyDown, handlePointerDown, handleVisibilityChange, ownerDocumentRef]);

  /**
   * Returns `true` if the event is called by keyboard key
   * @param {*} event
   */
  var handleFocusVisible = React.useCallback(event => {
    var {
      currentTarget
    } = event;
    try {
      currentTarget.matches(':focus-visible');
    } catch (e) {}
    return hadKeyboardEvent.current || focusTriggersKeyboardModality(currentTarget);
  }, []);
  var handleBlurVisible = () => {
    hadFocusVisibleRecently.current = true;
    clearTimeout(hadFocusVisibleRecentlyTimeout.current);
    hadFocusVisibleRecentlyTimeout.current = setTimeout(() => {
      hadFocusVisibleRecently.current = false;
    }, 100);
  };
  return {
    handleBlurVisible,
    handleFocusVisible,
    ref
  };
}